import {
  Divider,
  Drawer,
  DrawerProps,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ScanModel } from '../../network';
import { CompareModeEnum, useCompareStore } from './useCompareStore';
import { FC, RefObject, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { QualityClassChip, ScanStatusChip } from '../../ui/ScanStatusChips';

interface ScanCompareSelectorProps {
  scans: ScanModel[];
  containerRef: RefObject<HTMLDivElement>;
}

export const ScanCompareSelector: FC<ScanCompareSelectorProps> = ({
  scans,
  containerRef,
}) => {
  const {
    isScanSelectorOpen,
    setIsScanSelectorOpen,
    setCompareMode,
    rightScan,
    leftScan,
  } = useCompareStore();

  const selectedScanIds = useMemo(() => {
    const leftScanId = leftScan?.id;
    const rightScanId = rightScan?.id;

    if (leftScanId && rightScanId) {
      return [leftScanId, rightScanId];
    }

    return [leftScanId];
  }, [leftScan, rightScan]);

  const handleClose: DrawerProps['onClose'] = () => {
    // reset compare mode since the drawer closed without selecting a scan
    if (rightScan === null) {
      setCompareMode(CompareModeEnum.Single);
    }
    setIsScanSelectorOpen(null);
  };

  const isOpen = Boolean(isScanSelectorOpen);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={handleClose}
      container={containerRef.current}
    >
      <div style={{ width: '300px' }}>
        <div style={{ padding: '16px' }}>
          <Typography variant='h6'>Select a scan</Typography>
        </div>

        <List>
          {scans.map((scan) => (
            <ScanCompareSelectorItem
              key={scan.id}
              scan={scan}
              selected={selectedScanIds.includes(scan.id)}
            />
          ))}
        </List>
      </div>
    </Drawer>
  );
};

interface ScanCompareSelectorItemProps {
  scan: ScanModel;
  selected: boolean;
}

const ScanCompareSelectorItem: FC<ScanCompareSelectorItemProps> = ({
  scan,
  selected,
}) => {
  const { setRightScan, setCompareMode, setLeftScan, isScanSelectorOpen } =
    useCompareStore();
  const { primaryText } = useMemo(() => {
    return {
      primaryText: `${scan.id} - ${format(
        new Date(scan.created_at),
        'MM/dd/yyyy'
      )}`,
    };
  }, [scan]);

  const handleSelect = useCallback(async () => {
    try {
      if (isScanSelectorOpen === 'primary') {
        setLeftScan(scan.id);
      }

      if (isScanSelectorOpen === 'secondary') {
        setRightScan(scan.id);
      }
    } catch (error) {
      console.error(error);
      setRightScan(null);
      setCompareMode(CompareModeEnum.Single);
    }
  }, [isScanSelectorOpen, setLeftScan, setRightScan, scan.id, setCompareMode]);

  return (
    <>
      <ListItem
        selected={selected}
        onClick={handleSelect}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
          },
        }}
      >
        <ListItemText>
          <div>
            <Typography variant='body1'>{primaryText}</Typography>

            <div style={{ gap: '8px', display: 'flex' }}>
              <ScanStatusChip status={scan?.status} />
              <QualityClassChip
                qualityClass={scan?.predicted_bent_deviation_mesh_quality_class}
              />
            </div>
          </div>
        </ListItemText>
      </ListItem>
      <Divider component='li' />
    </>
  );
};
